import { getBaseQuery, getBaseUrl } from '../api.util.ts'
import { createApi } from '@reduxjs/toolkit/query/react'
import {
  GetBookSnapshotRequest,
  GetBookSnapshotResponse,
  GetBuyingPowerRequest,
  GetBuyingPowerResponse,
  GetDailyPnlRequest,
  GetDailyPnlResponse,
  GetDeploymentCurrentRequest,
  GetDeploymentNetExposureRequest,
  GetDeploymentNetExposureResponse,
  GetDeploymentResponse,
  GetFullyPnlRequest,
  GetFullyPnlResponse,
  GetHitRateRequest,
  GetHitRateResponse,
  GetStatisticsRequest,
  GetStatisticsResponse,
  GetTopBottomPerformingIdeasRequest,
  GetTopBottomPerformingIdeasResponse,
  GetTotalNewIdeasRequest,
  GetTotalNewIdeasResponse,
} from './statistics-api.def.ts'

const interfaceName = 'statistics'

export const statisticsApi = createApi({
  reducerPath: 'statisticsApi',
  baseQuery: getBaseQuery(getBaseUrl(interfaceName)),
  tagTypes: ['statistics', 'current-deployment'],
  endpoints: (build) => ({
    getDailyPnl: build.query<GetDailyPnlResponse, GetDailyPnlRequest>({
      query: (params) => ({
        url: `/${params.bookId}/daily-pnl`,
        method: 'GET',
        params: params,
      }),
    }),
    getTopBottomPerformingIdeas: build.query<
      GetTopBottomPerformingIdeasResponse,
      GetTopBottomPerformingIdeasRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/top-and-bottom-ideas`,
        method: 'GET',
        params: params,
      }),
    }),
    getHitRate: build.query<GetHitRateResponse, GetHitRateRequest>({
      query: (params) => ({
        url: `/${params.bookId}/hit-rate`,
        method: 'GET',
        params: params,
      }),
    }),
    getDeploymentCurrent: build.query<
      GetDeploymentResponse,
      GetDeploymentCurrentRequest
    >({
      query: (params) => ({
        url: `/deployment/${params.bookId}/current`,
        method: 'GET',
        params: params,
      }),
      providesTags: ['current-deployment'],
    }),
    getTotalNewIdeasDateRange: build.query<
      GetTotalNewIdeasResponse,
      GetTotalNewIdeasRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/total-new-ideas`,
        method: 'GET',
        params: params,
      }),
    }),
    getStatistics: build.query<GetStatisticsResponse, GetStatisticsRequest>({
      query: (params) => ({
        url: '/',
        method: 'GET',
        params: params,
      }),
    }),
    getBuyingPower: build.query<GetBuyingPowerResponse, GetBuyingPowerRequest>({
      query: (params) => ({
        url: `/${params.bookId}/buying-power`,
        method: 'GET',
        params: params,
      }),
    }),
    getNetExposure: build.query<
      GetDeploymentNetExposureResponse,
      GetDeploymentNetExposureRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/net-exposure`,
        method: 'GET',
        params: params,
      }),
    }),
    getFullyPnl: build.query<GetFullyPnlResponse, GetFullyPnlRequest>({
      query: (params) => ({
        url: `/${params.bookId}/fully-pnl`,
        method: 'GET',
        params: params,
      }),
    }),
    getBookSnapshot: build.query<
      GetBookSnapshotResponse,
      GetBookSnapshotRequest
    >({
      query: (params) => ({
        url: `/${params.bookId}/snapshot/${params.date}`,
        method: 'GET',
        params: params,
      }),
    }),
  }),
})

export const {
  useGetDailyPnlQuery,
  useGetTopBottomPerformingIdeasQuery,
  useGetHitRateQuery,
  useGetDeploymentCurrentQuery,
  useGetTotalNewIdeasDateRangeQuery,
  useGetStatisticsQuery,
  useGetBuyingPowerQuery,
  useGetNetExposureQuery,
  useGetFullyPnlQuery,
  useLazyGetBookSnapshotQuery,
} = statisticsApi
